import CloseIcon from '@mui/icons-material/Close';
import DownloadOutlinedIcon from '@mui/icons-material/DownloadOutlined';
import FolderZipOutlinedIcon from '@mui/icons-material/FolderZipOutlined';
import {
  Alert, Autocomplete, Box, Button, CircularProgress, Dialog, DialogContent, Divider,
  IconButton, List, ListItemButton, ListItemText, Slider, TextField, Typography,
} from '@mui/material';
import { QRCodeSVG } from 'qrcode.react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useGetList, useNotify } from 'react-admin';
import FeatureIntro from '../../../components/FeatureIntro';
import StationPlate from './StationPlate';
import { artFileAsImage, saveBlob } from '../lib/download';
import { createImagePdf } from '../lib/pdf';
import { drawPlate, qrSourceOf } from '../lib/plateCanvas';
import { plateQrValue } from '../lib/plateGeometry';
import { createZip, ZipEntry } from '../lib/zip';
import { AppArtFile, ForecourtFuelPoint, PlateRecipe, PlateStation } from '../types';

/**
 * A placa composta, com o código editável e a peça pronta para baixar.
 *
 * O gabarito sai do gerador com os dois espaços vazios; quem os preenche é o
 * admin. Aqui dá para digitar o código de um posto e ver a peça inteira antes
 * de mandar imprimir — que é a única forma de descobrir que um código longo
 * não cabe na tarja, ou que o QR ficou pequeno demais para a distância que a
 * bomba exige.
 *
 * Os códigos REAIS vêm do mapa de pista: escolhido o posto, a lista de bombas é
 * a mesma consulta da aba "Mapa de Pista" do show do posto (`/station-qrcodes`
 * com os IDs numéricos da ConectTec), e o zip sai com uma placa por bomba, em
 * PDF de impressão e em PNG — cada formato na sua pasta.
 *
 * O diálogo é a casca. A composição mora em dois lugares que não conhecem nem
 * `AppArtFile` nem diálogo nenhum: o `StationPlate` compõe em DOM, para o
 * palco, e o `drawPlate` compõe em canvas, que é de onde os arquivos saem.
 */

const slug = (text: string) => text.toLowerCase().normalize('NFD')
  .replace(/[̀-ͯ]/g, '').replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '') || 'placa';

/** O código do `placa-exemplo.png`, para o diálogo abrir mostrando a peça montada. */
const EXAMPLE_CODE = 'ABC1-2345';

/**
 * Até onde o slider deixa o QR encolher, em % do quadrado da receita. É ajuste
 * fino de margem: bem abaixo disso o QR fica pequeno para a distância da bomba,
 * que é justamente o que o preview existe para evitar.
 */
const QR_SIZE_MIN = 70;

/**
 * A folha e a placa do PDF, as mesmas do PDF modelo de impressão: folha de
 * 936×1370 pt (330×483 mm, um A3+) com a placa centralizada em tamanho real,
 * 552,756 pt de lado (195 mm) — o artboard do modelo CTTX no Illustrator.
 * Em ponto e não em pixel: é o que faz a placa sair na medida certa na gráfica,
 * qualquer que seja a resolução do gabarito.
 */
const PRINT_SHEET = { w: 936, h: 1370 };
const PRINT_PLATE_WIDTH = 552.756;

/**
 * O formato que o app reconhece. O leitor do app pega o que vem depois da
 * última `/` do QR e só aceita 7 dígitos (`QrCodeCamera`) — a digitação manual
 * tem a mesma trava (`CodeInclude`). Código fora disso imprime uma placa que
 * o app não lê.
 */
const APP_CODE = /^\d{7}$/;

const byNumber = (a: number | string, b: number | string) =>
  String(a).localeCompare(String(b), undefined, { numeric: true });

const fuelsOf = (pump: ForecourtFuelPoint) => [...new Set(
  pump.hoses.map(hose => hose.fuel?.name).filter(Boolean),
)].join(', ');

const pngOf = (canvas: HTMLCanvasElement) => new Promise<Blob>((resolve, reject) => {
  canvas.toBlob(blob => (blob ? resolve(blob) : reject(new Error('Não foi possível gerar a imagem'))), 'image/png');
});

/* O PDF sai do MESMO canvas do PNG: uma captura só por placa, e os dois
   arquivos não têm como divergir. */
const pdfOf = async (canvas: HTMLCanvasElement) => {
  const context = canvas.getContext('2d');
  if (!context) { throw new Error('Não foi possível gerar o PDF'); }
  return createImagePdf({
    pixels: context.getImageData(0, 0, canvas.width, canvas.height),
    page: PRINT_SHEET,
    width: PRINT_PLATE_WIDTH,
  });
};

/** Uma placa a rasterizar: o código e o nome, sem extensão, dos arquivos que ela vira. */
interface Plate { code: string; baseName: string }

type PlateFormat = 'png' | 'pdf';

/**
 * O que está sendo composto. Com `zipName`, as placas vão empacotadas, cada
 * uma em PNG e em PDF; sem ele, a placa única desce solta no `format`.
 *
 * A receita e o tamanho do QR entram AQUI, congelados no clique, em vez de
 * serem lidos do estado na hora de desenhar: o lote leva segundos, e nada do
 * que mudar no diálogo nesse meio tempo pode mudar o que já está saindo.
 */
interface Job {
  /** O gabarito já decodificado — o mesmo serve para o lote inteiro. */
  art: ImageBitmap | HTMLImageElement;
  recipe: PlateRecipe;
  qrScale: number;
  plates: Plate[];
  zipName?: string;
  format?: PlateFormat;
}

interface Props {
  open: boolean;
  onClose: () => void;
  /** A linha da placa. A geometria vem do `recipe` dela. */
  file: AppArtFile | null;
  recipe: PlateRecipe | null;
  chainId: string;
  chainName: string;
}

const StationPlateDialog = ({ open, onClose, file, recipe, chainId, chainName }: Props) => {
  const notify = useNotify();
  const [code, setCode] = useState(EXAMPLE_CODE);
  /** Tamanho do QR em % do quadrado da receita — o valor do slider. */
  const [qrSize, setQrSize] = useState(100);
  const [station, setStation] = useState<PlateStation | null>(null);
  const [job, setJob] = useState<Job | null>(null);
  const [progress, setProgress] = useState<{ done: number; total: number } | null>(null);
  /** Qual botão está trabalhando — do clique até o arquivo descer. */
  const [running, setRunning] = useState<PlateFormat | 'zip' | null>(null);
  /* Os QR do lote, montados fora da tela só para serem LIDOS — ver o bloco no
     fim do render. */
  const qrRefs = useRef<(SVGSVGElement | null)[]>([]);

  /* Reabrir por outra placa tem de começar no exemplo, e não no código que
     sobrou da anterior — senão a peça nova aparece com o código de um posto
     que não é o dela. */
  useEffect(() => {
    if (open) { setCode(EXAMPLE_CODE); setStation(null); setQrSize(100); }
  }, [open, file?.id]);

  /* `status: null` tira o filtro de ativos, que é o padrão do `/stations`
     quando `status` não vem. Posto nasce com `is_active = false`, e a placa é
     impressa justamente antes de o posto entrar no ar — com o filtro, a rede em
     implantação abria o select vazio. */
  const { data: stations, isPending: loadingStations, error: stationsError } = useGetList<PlateStation>(
    'stations',
    {
      filter: { chainId, status: null },
      pagination: { page: 1, perPage: 9999 },
      sort: { field: 'name', order: 'ASC' },
    },
    { enabled: open && !!chainId },
  );

  const numericIds = station?.numericIds || [];
  const { data: fuelPoints, isPending: pumpsPending, error: pumpsError } = useGetList<ForecourtFuelPoint>(
    'station-qrcodes',
    { filter: { numericIds }, pagination: { page: 1, perPage: 1000 } },
    { enabled: open && numericIds.length > 0 },
  );
  const loadingPumps = numericIds.length > 0 && pumpsPending;

  /* Bomba sem bico numerado fica de fora, igual à aba Mapa de Pista: é ponto
     que a ConectTec lista mas que não abastece — placa para ela seria lixo na
     pista. */
  const pumps = useMemo(() => (fuelPoints || [])
    .filter(pump => pump.hoses?.some(hose => hose.hoseNumber))
    .sort((a, b) => byNumber(a.stationNumericId, b.stationNumericId)
      || byNumber(a.fuelPointNumber, b.fuelPointNumber)),
  [fuelPoints]);

  const hiddenPumps = (fuelPoints?.length || 0) - pumps.length;
  const offPattern = pumps.filter(pump => !APP_CODE.test(pump.qrcode)).length;

  /* Com o posto escolhido, o palco já mostra a primeira bomba de verdade em vez
     do código de mentira. */
  useEffect(() => {
    if (pumps.length) { setCode(pumps[0].qrcode); }
  }, [pumps]);

  const busy = !!running;

  /**
   * Baixar, em dois passos.
   *
   * Primeiro o gabarito é decodificado UMA vez e os QR de todos os códigos vão
   * para fora da tela. Só no render seguinte, com eles montados, é que o efeito
   * abaixo compõe as placas — o desvio pelo React existe porque o codificador
   * de QR da lib só é alcançável pelo componente.
   */
  const start = async (plates: Plate[], target: { zipName: string } | { format: PlateFormat }) => {
    if (!file || !recipe || !plates.length) { return; }
    setRunning('zipName' in target ? 'zip' : target.format);
    try {
      setJob({
        art: await artFileAsImage(file), recipe, qrScale: qrSize / 100, plates, ...target,
      });
    } catch (error: any) {
      notify(error?.message || 'Não foi possível preparar a imagem', { type: 'error' });
      setRunning(null);
    }
  };

  const downloadOne = (format: PlateFormat) => start(
    [{ code, baseName: `placa-${slug(chainName)}-${slug(code)}` }],
    { format },
  );

  const downloadStation = () => {
    if (!station) { return; }
    const used = new Set<string>();
    const plates = pumps.map(pump => {
      const base = `bomba-${String(pump.fuelPointNumber).padStart(2, '0')}-${slug(pump.qrcode)}`;
      let baseName = base;
      for (let n = 2; used.has(baseName); n++) { baseName = `${base}-${n}`; }
      used.add(baseName);
      return { code: pump.qrcode, baseName };
    });
    start(plates, { zipName: `placas-${slug(chainName)}-${slug(station.name)}.zip` });
  };

  useEffect(() => {
    if (!job) { return; }

    (async () => {
      // UM canvas para o lote inteiro, no tamanho nativo da receita: o PNG sai
      // com o mesmo lado que o gerador produziu. Ampliar aqui só interpolaria
      // um gabarito que não tem mais resolução a dar.
      const canvas = document.createElement('canvas');
      canvas.width = job.recipe.canvas.w;
      canvas.height = job.recipe.canvas.h;
      const context = canvas.getContext('2d');

      try {
        if (!context) { throw new Error('Não foi possível gerar a imagem'); }

        // Um formato por pasta: o que vai para a gráfica é o `pdf/` inteiro, e
        // com os dois misturados na raiz alguém acaba mandando PNG junto.
        const pdfs: ZipEntry[] = [];
        const pngs: ZipEntry[] = [];
        setProgress({ done: 0, total: job.plates.length });
        // Uma de cada vez, e os dois arquivos de uma placa saem ANTES de a
        // próxima ser desenhada por cima — é o mesmo canvas.
        for (const [index, plate] of job.plates.entries()) {
          drawPlate(context, {
            art: job.art,
            recipe: job.recipe,
            code: plate.code,
            qr: qrSourceOf(qrRefs.current[index]),
            qrScale: job.qrScale,
          });

          if (!job.zipName) {
            const format = job.format || 'png';
            saveBlob(await (format === 'pdf' ? pdfOf(canvas) : pngOf(canvas)), `${plate.baseName}.${format}`);
            return;
          }
          const [png, pdf] = await Promise.all([pngOf(canvas), pdfOf(canvas)]);
          pdfs.push({ name: `pdf/${plate.baseName}.pdf`, content: new Uint8Array(await pdf.arrayBuffer()) });
          pngs.push({ name: `png/${plate.baseName}.png`, content: new Uint8Array(await png.arrayBuffer()) });
          setProgress({ done: index + 1, total: job.plates.length });
        }
        saveBlob(createZip([...pdfs, ...pngs]), job.zipName!);
      } catch (error: any) {
        notify(error?.message || 'Não foi possível gerar as placas', { type: 'error' });
      } finally {
        // O bitmap segura os pixels decodificados do gabarito até alguém
        // fechá-lo; o lote acabou.
        if ('close' in job.art) { job.art.close(); }
        setJob(null);
        setProgress(null);
        setRunning(null);
      }
    })();
  }, [job, notify]);

  const art = file ? (file.previewUrl || file.fileUrl) : null;

  const darkField = {
    '& .MuiInputBase-input': { color: 'grey.100' },
    '& .MuiInputLabel-root': { color: 'grey.400' },
    '& .MuiFormHelperText-root': { color: 'grey.500' },
    '& .MuiSvgIcon-root': { color: 'grey.400' },
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogContent sx={{ backgroundColor: 'grey.900', p: 0 }}>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', minHeight: 560 }}>

          {/* Palco */}
          <Box sx={{
            flex: '1 1 380px', display: 'flex', alignItems: 'center',
            justifyContent: 'center', p: 3,
          }}>
            {recipe ? (
              <StationPlate art={art} recipe={recipe} code={code} width={380} qrScale={qrSize / 100} />
            ) : (
              <Alert severity="warning" sx={{ maxWidth: 340 }}>
                Esta placa não trouxe a geometria dos espaços. Ela é de uma rodada
                anterior à receita — gere a placa de novo para compor o QR e o código
                por cima.
              </Alert>
            )}
          </Box>

          {/* Controles */}
          <Box sx={{
            flex: '0 0 320px', backgroundColor: 'grey.800', color: 'grey.100',
            p: 2.5, display: 'flex', flexDirection: 'column', gap: 2,
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Typography variant="subtitle2">Placa de posto</Typography>
              <IconButton size="small" onClick={onClose} sx={{ color: 'grey.400' }}>
                <CloseIcon fontSize="small" />
              </IconButton>
            </Box>

            <Divider sx={{ borderColor: 'grey.700' }} />

            <Autocomplete
              options={stations || []}
              value={station}
              onChange={(_, value) => setStation(value)}
              getOptionLabel={option => (option.isActive === false ? `${option.name} (inativo)` : option.name)}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              loading={loadingStations}
              disabled={busy}
              size="small"
              noOptionsText={stationsError ? 'Não foi possível carregar os postos' : 'Nenhum posto cadastrado nesta rede'}
              renderInput={params => (
                <TextField
                  {...params}
                  label="Posto"
                  variant="filled"
                  helperText={stationsError
                    ? `Falha ao carregar os postos: ${(stationsError as Error).message || 'erro desconhecido'}`
                    : 'Os códigos vêm do mapa de pista do posto.'}
                  sx={darkField}
                />
              )}
            />

            {station && !numericIds.length && (
              <Alert severity="warning">
                Este posto não tem ID numérico da ConectTec cadastrado — sem ele não há
                como consultar o mapa de pista.
              </Alert>
            )}

            {loadingPumps && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CircularProgress size={14} />
                <Typography variant="caption" sx={{ color: 'grey.400' }}>Consultando o mapa de pista…</Typography>
              </Box>
            )}

            {/* O controller devolve lista vazia quando a ConectTec falha, então
                "sem bomba" e "consulta caiu" chegam iguais aqui. */}
            {!!numericIds.length && !loadingPumps && (pumpsError || !pumps.length) && (
              <Alert severity="warning">
                A ConectTec não devolveu nenhuma bomba com bico para os IDs {numericIds.join(', ')}.
                A aba Mapa de Pista do posto mostra o mesmo vazio.
              </Alert>
            )}

            {!!pumps.length && (
              <Box>
                <Typography variant="caption" sx={{ color: 'grey.500' }}>
                  {pumps.length} bomba(s) — clique para ver a placa
                </Typography>
                <List dense disablePadding sx={{ maxHeight: 180, overflowY: 'auto', mt: 0.5 }}>
                  {pumps.map(pump => (
                    <ListItemButton
                      key={`${pump.stationNumericId}-${pump.fuelPointNumber}-${pump.qrcode}`}
                      selected={pump.qrcode === code}
                      onClick={() => setCode(pump.qrcode)}
                      sx={{ borderRadius: 1, '&.Mui-selected': { backgroundColor: 'grey.700' } }}
                    >
                      <ListItemText
                        primary={`Bomba ${pump.fuelPointNumber} · ${pump.qrcode}`}
                        secondary={fuelsOf(pump) || undefined}
                        slotProps={{
                          primary: { sx: { color: APP_CODE.test(pump.qrcode) ? 'grey.100' : 'warning.light' } },
                          secondary: { sx: { color: 'grey.500' } },
                        }}
                      />
                    </ListItemButton>
                  ))}
                </List>
                {!!hiddenPumps && (
                  <Typography variant="caption" sx={{ color: 'grey.500', display: 'block' }}>
                    {hiddenPumps} bomba(s) sem bico ficaram de fora, como na aba Mapa de Pista.
                  </Typography>
                )}
                {!!offPattern && (
                  <Typography variant="caption" sx={{ color: 'warning.light', display: 'block' }}>
                    {offPattern} código(s) não têm 7 dígitos — o leitor de QR do app não reconhece.
                  </Typography>
                )}
              </Box>
            )}

            <TextField
              label="Código de abastecimento"
              value={code}
              onChange={event => setCode(event.target.value.toUpperCase())}
              size="small"
              variant="filled"
              disabled={busy}
              helperText="Código longo encolhe sozinho para não encostar na borda da tarja."
              sx={darkField}
            />

            {/* Desabilitado durante o download: a captura fotografa as placas
                fora da tela com o valor do momento, e mexer no meio faria o zip
                sair com tamanhos misturados. */}
            <Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography variant="caption" sx={{ color: 'grey.400' }}>Tamanho do QR</Typography>
                <Typography variant="caption" sx={{ color: 'grey.100' }}>{qrSize}%</Typography>
              </Box>
              <Slider
                value={qrSize}
                onChange={(_, value) => setQrSize(value as number)}
                min={QR_SIZE_MIN}
                max={100}
                step={1}
                size="small"
                disabled={busy || !recipe}
                aria-label="Tamanho do QR"
              />
              <Typography variant="caption" sx={{ color: 'grey.500', display: 'block' }}>
                Encolhe o QR dentro do quadro, abrindo margem branca em volta. Vale para esta placa e para o zip.
              </Typography>
            </Box>

            <Box>
              <Typography variant="caption" sx={{ color: 'grey.500' }}>O QR aponta para</Typography>
              <Typography variant="body2" sx={{ wordBreak: 'break-all' }}>
                {plateQrValue(code)}
              </Typography>
            </Box>

            <Box sx={{ flex: 1 }} />

            <Box>
              <Typography variant="caption" sx={{ color: 'grey.500' }}>Baixar esta placa</Typography>
              <Box sx={{ display: 'flex', gap: 1, mt: 0.5 }}>
                {(['png', 'pdf'] as const).map(format => (
                  <Button
                    key={format}
                    fullWidth
                    variant="outlined"
                    startIcon={running === format ? <CircularProgress size={14} /> : <DownloadOutlinedIcon />}
                    onClick={() => downloadOne(format)}
                    disabled={busy || !recipe || !file || !code}
                  >
                    {format.toUpperCase()}
                  </Button>
                ))}
              </Box>
            </Box>

            <FeatureIntro
              id="app-art-station-plates-zip"
              title="Todas as placas de um posto de uma vez"
              description="Escolha o posto acima: os códigos de cada bomba vêm do mapa de pista, e o zip desce com uma placa por bomba, em PDF e em PNG (cada formato na sua pasta), já com o QR e o código preenchidos."
              anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
              transformOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
              <Button
                fullWidth
                variant="contained"
                startIcon={running === 'zip' ? <CircularProgress size={14} /> : <FolderZipOutlinedIcon />}
                onClick={downloadStation}
                disabled={busy || !recipe || !file || !pumps.length}
              >
                {running === 'zip'
                  ? (progress ? `Montando ${progress.done}/${progress.total}…` : 'Preparando…')
                  : `Baixar placas do posto (.zip)${pumps.length ? ` (${pumps.length})` : ''}`}
              </Button>
            </FeatureIntro>
          </Box>
        </Box>

        {/* Os QR do lote, montados só para serem LIDOS.

            O `qrcode.react` não exporta o codificador, só os componentes — e
            reimplantar QR à mão seria manter um segundo codificador que pode
            discordar do que o palco mostra. Então o lote monta um por bomba e o
            `qrSourceOf` lê deles o desenho dos módulos.

            `size` não importa: o que se lê é o `viewBox` e o path, ambos em
            unidades de módulo, e quem escolhe o tamanho final é o `drawPlate`.
            O que importa é `value`, `level` e `marginSize` — é só disso que o
            desenho do código depende. `visibility: hidden` para o browser não
            gastar rasterizando dezenas de QR que ninguém vai ver.

            `level="M"` casa com o padrão do `qrcode` do gerador — a lib do
            admin usa "L" por padrão, e a diferença muda o desenho do código. */}
        {job && (
          <Box sx={{ position: 'fixed', left: -20000, top: 0, visibility: 'hidden' }}>
            {job.plates.map((plate, index) => (
              <QRCodeSVG
                key={plate.baseName}
                ref={node => { qrRefs.current[index] = node; }}
                value={plateQrValue(plate.code)}
                size={1}
                level="M"
                marginSize={0}
              />
            ))}
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default StationPlateDialog;
